import { z } from 'zod';

import type {
  GetShipmentSourceCompositionRequest,
  GetShipmentSourceCompositionResponse,
  ShipmentSourceCompositionMaterialBucket,
  ShipmentSourceCompositionPreviewMeta,
  ShipmentSourceCompositionPurchaseGroupPreviewItem,
  ShipmentSourceCompositionPurchaseGroupsPreview,
  ShipmentSourceCompositionSummary,
} from './get-shipment-source-composition.dto.js';
import {
  ShipmentReportDailyRowSchema,
  ShipmentReportRowValuesSchema,
} from './shipments-shared.schema.js';
import { IsoDateTimeSchema, PresentationTypeSchema } from './shipments-types.js';

const nonNegativeIntSchema = z.number().int().min(0);

export const ShipmentSourceCompositionCapReasonSchema = z.literal('purchase_group_preview');

export const ShipmentSourceCompositionPartialReasonSchema = z.literal(
  'source_projection_details_missing',
);

export const GetShipmentSourceCompositionRequestSchema: z.ZodType<GetShipmentSourceCompositionRequest> =
  z
    .object({
      companyId: z.uuid(),
      shipmentId: z.uuid(),
    })
    .strict();

export const ShipmentSourceCompositionSummarySchema: z.ZodType<ShipmentSourceCompositionSummary> = z
  .object({
    rangeStartAt: IsoDateTimeSchema,
    rangeEndAt: IsoDateTimeSchema,
    reportDayCount: nonNegativeIntSchema,
    sourceRecordCount: nonNegativeIntSchema,
    purchaseGroupCount: nonNegativeIntSchema,
    materialBucketCount: nonNegativeIntSchema,
    totalsIncludeAllRecords: z.literal(true),
    totals: ShipmentReportRowValuesSchema,
  })
  .strict();

export const ShipmentSourceCompositionMaterialBucketSchema: z.ZodType<ShipmentSourceCompositionMaterialBucket> =
  z
    .object({
      materialId: z.uuid(),
      presentationType: PresentationTypeSchema,
      sourceRecordCount: nonNegativeIntSchema,
      totals: ShipmentReportRowValuesSchema,
    })
    .strict();

export const ShipmentSourceCompositionPreviewMetaSchema: z.ZodType<ShipmentSourceCompositionPreviewMeta> =
  z
    .object({
      returnedCount: nonNegativeIntSchema,
      totalCount: nonNegativeIntSchema,
      limit: z.number().int().positive(),
      hasMore: z.boolean(),
      capReason: ShipmentSourceCompositionCapReasonSchema,
    })
    .strict()
    .superRefine((value, ctx) => {
      if (value.returnedCount > value.limit) {
        ctx.addIssue({
          code: 'custom',
          message: 'returnedCount must be less than or equal to limit.',
          path: ['returnedCount'],
        });
      }
      if (value.returnedCount > value.totalCount) {
        ctx.addIssue({
          code: 'custom',
          message: 'returnedCount must be less than or equal to totalCount.',
          path: ['returnedCount'],
        });
      }
      if (value.hasMore !== value.returnedCount < value.totalCount) {
        ctx.addIssue({
          code: 'custom',
          message: 'hasMore must match returnedCount and totalCount.',
          path: ['hasMore'],
        });
      }
    });

export const ShipmentSourceCompositionPurchaseGroupPreviewItemSchema: z.ZodType<ShipmentSourceCompositionPurchaseGroupPreviewItem> =
  z
    .object({
      sequence: z.number().int().positive(),
      materialId: z.uuid(),
      occurredAtStart: IsoDateTimeSchema.nullable(),
      occurredAtEnd: IsoDateTimeSchema.nullable(),
      sourceRecordCount: nonNegativeIntSchema,
      totals: ShipmentReportRowValuesSchema,
    })
    .strict();

export const ShipmentSourceCompositionPurchaseGroupsPreviewSchema: z.ZodType<ShipmentSourceCompositionPurchaseGroupsPreview> =
  z
    .object({
      items: z.array(ShipmentSourceCompositionPurchaseGroupPreviewItemSchema),
      meta: ShipmentSourceCompositionPreviewMetaSchema,
    })
    .strict()
    .superRefine((value, ctx) => {
      if (value.items.length !== value.meta.returnedCount) {
        ctx.addIssue({
          code: 'custom',
          message: 'items length must match returnedCount.',
          path: ['meta', 'returnedCount'],
        });
      }
    });

export const GetShipmentSourceCompositionResponseSchema: z.ZodType<GetShipmentSourceCompositionResponse> =
  z
    .object({
      shipmentId: z.uuid(),
      generatedAt: IsoDateTimeSchema,
      summary: ShipmentSourceCompositionSummarySchema,
      dailyBuckets: z.array(ShipmentReportDailyRowSchema),
      materialBuckets: z.array(ShipmentSourceCompositionMaterialBucketSchema),
      purchaseGroupsPreview: ShipmentSourceCompositionPurchaseGroupsPreviewSchema,
      partialReasons: z.array(ShipmentSourceCompositionPartialReasonSchema),
    })
    .strict();
